import { memo, useState, useRef, useEffect } from 'react';
import { resolveCustomRange, getDataBounds } from '../utils/customRange';
import styles from './CustomRangeControl.module.css';

/**
 * Mobile-only collapsible control that lets the user jump both charts to an
 * arbitrary exact date range (e.g. "2000-01-01 to 2010-12-31") — something
 * the fixed "N years back from today" range-selector presets can't express,
 * and which the native rangeSelector date inputs used to cover before they
 * were disabled on mobile for overflow (see chartConfig.js / ratioChartConfig.js).
 *
 * Deliberately does NOT touch Highcharts' own `rangeSelector`/`inputEnabled`
 * — any `chart.update()` call that includes a `rangeSelector` key destroys
 * and reinits Highcharts' RangeSelector, which has previously corrupted this
 * app's default date range on mobile (see ratioChartConfig.js's
 * `getRatioRangeSelector` docstring, commit 85a1d1f). Instead this calls
 * `xAxis[0].setExtremes(...)` directly on both chart refs, exactly like
 * useStockData.js's adaptive-default-range logic does on every fresh fetch —
 * an independent path that never goes near `rangeSelector` at all.
 *
 * Hidden entirely above the same ~768px breakpoint the mobile chart rules
 * use (see CustomRangeControl.module.css) — desktop already has Highcharts'
 * native date inputs, so this control has nothing to add there.
 */
const CustomRangeControl = ({ chartRef, ratioChartRef }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [message, setMessage] = useState(null);
  const containerRef = useRef(null);

  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (event) => {
      if (containerRef.current && !containerRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, [isOpen]);

  const handleToggle = () => {
    setIsOpen((prev) => !prev);
    setMessage(null);
  };

  const handleApply = (event) => {
    event.preventDefault();

    const mainChart = chartRef?.current?.chart;
    const ratioChart = ratioChartRef?.current?.chart;
    const bounds = getDataBounds(mainChart);
    const result = resolveCustomRange(start, end, bounds);

    if (result.error) {
      setMessage(result.error);
      return;
    }

    // This control deliberately applies the SAME range to both charts at
    // once (the two charts' windows are otherwise independent — see
    // useAxisSync.js). Setting extremes on each ref fires that chart's own
    // afterSetExtremes listener, which recomputes only that chart's overlays/
    // y-axis. redraw:true/animation:false so each behaves like any other zoom
    // action (rangeSelector click, drag).
    mainChart?.xAxis[0]?.setExtremes(result.start, result.end, true, false);
    ratioChart?.xAxis[0]?.setExtremes(result.start, result.end, true, false);

    setMessage(result.clamped ? 'Adjusted to available data.' : null);
  };

  return (
    <div className={styles.container} ref={containerRef}>
      <button
        type="button"
        className={styles.toggle}
        aria-expanded={isOpen}
        onClick={handleToggle}
      >
        Custom range {isOpen ? '▴' : '▾'}
      </button>

      {isOpen && (
        <form className={styles.panel} onSubmit={handleApply}>
          <label className={styles.field}>
            Start
            <input
              type="date"
              value={start}
              onChange={(e) => setStart(e.target.value)}
              aria-label="Custom range start date"
            />
          </label>
          <label className={styles.field}>
            End
            <input
              type="date"
              value={end}
              onChange={(e) => setEnd(e.target.value)}
              aria-label="Custom range end date"
            />
          </label>
          <button type="submit" className={styles.applyButton}>Apply</button>
          {message && <p className={styles.message} role="status">{message}</p>}
        </form>
      )}
    </div>
  );
};

export default memo(CustomRangeControl);
